import React from 'react'
import { Routes, Route, Navigate } from 'react-router'

import './hooks/useTheme' // module-level theme init
import ErrorBoundary from './components/ErrorBoundary'
import { FlowiumProvider } from './client/FlowiumContext'
import AppContainer from './layout/AppContainer'
import AuthRoute from './auth/AuthRoute';
import { API_URL_CLIENT, API_URL_CLIENT_FALLBACK } from './config/api'

import LoadingPage from './components/LoadingPage';
import Login from './auth/Login';
import PasswordRecovery from './auth/PasswordRecovery';
import PasswordResetPage from './auth/PasswordResetPage';
import PlanCancel from './settings/PlanCancel';
import PlanSuccess from './settings/PlanSuccess';
import PrivateRoute from './auth/PrivateRoute';
import Register from './auth/Register';
import Verify from './auth/Verify';
import { DetailsPanelProps } from './layout/AppContainer';
import SettingsLayout from './settings/SettingsLayout';

// Lazy-loaded route components for code splitting
const FileBrowser = React.lazy(() => import('./files/FileBrowser'));
const FileEditor = React.lazy(() => import('./files/FileEditor'));
const NotificationSettings = React.lazy(() => import('./settings/NotificationSettings'));
const AccountSettings = React.lazy(() => import('./settings/AccountSettings'));
const PlansBillingSettings = React.lazy(() => import('./settings/PlansBillingSettings'));
const ApiKeysSettings = React.lazy(() => import('./settings/ApiKeysSettings'));
const WidgetManagement = React.lazy(() => import('./settings/WidgetManagement'));
const AppearanceSettings = React.lazy(() => import('./settings/AppearanceSettings'));

const App: React.FC = () => {
    const [currentPath, setCurrentPath] = React.useState<string>('');

    return (
        <ErrorBoundary>
                <FlowiumProvider
            baseUrl={API_URL_CLIENT}
            fallbackBaseUrl={API_URL_CLIENT_FALLBACK}
        >
            <React.Suspense fallback={<LoadingPage />}>
            <Routes>
                <Route path="/" element={<AuthRoute />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify" element={<Verify />} />
                <Route path="/password-recovery" element={<PasswordRecovery />} />
                <Route path="/password-reset" element={<PasswordResetPage />} />
                <Route path="/browser" element={
                    <PrivateRoute>
                        <AppContainer title="Browser" padding="p-0" currentPath={currentPath} showBetaLaunchBanner>
                            {(props: DetailsPanelProps) => <FileBrowser {...props} onPathChange={setCurrentPath} />}
                        </AppContainer>
                    </PrivateRoute>
                } />
                <Route path="/editor" element={<PrivateRoute><AppContainer title="Editor"><FileEditor /></AppContainer></PrivateRoute>} />
                <Route path="/settings" element={
                    <PrivateRoute>
                        <AppContainer title="Profile settings">
                            <SettingsLayout />
                        </AppContainer>
                    </PrivateRoute>
                }>
                    <Route index element={<Navigate to="account" replace />} />
                    <Route path="account" element={<AccountSettings />} />
                    <Route path="plan" element={<PlansBillingSettings />} />
                    <Route path="plans" element={<Navigate to="/settings/plan" replace />} />
                    <Route path="api-keys" element={<ApiKeysSettings />} />
                    <Route path="widgets" element={<WidgetManagement />} />
                    <Route path="notifications" element={<NotificationSettings />} />
                    <Route path="password" element={<Navigate to="/settings/account" replace />} />
                    <Route path="profile" element={<Navigate to="/settings/account" replace />} />
                    <Route path="appearance" element={<AppearanceSettings />} />
                </Route>
                <Route path="/plan-success" element={<PrivateRoute><AppContainer title="Plan Success"><PlanSuccess /></AppContainer></PrivateRoute>} />
                <Route path="/plan-cancel" element={<PrivateRoute><AppContainer title="Plan Cancel"><PlanCancel /></AppContainer></PrivateRoute>} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            </React.Suspense>
        </FlowiumProvider>
        </ErrorBoundary>
    );
};

export default App;
